import React, {
	useState,
	useEffect,
	useRef,
	useCallback,
} from 'react';
import {
	List,
	Typography,
	Button,
	Modal,
	InputNumber,
	Tooltip,
	Alert,
	message,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { Balloon } from '../../../../interfaces/Ballooning';
import { InspectionSample, InspectionResult } from '../../../../interfaces/Quality.Interface';
import { useUpdateInspectionResultMutation } from '../../../../redux/api/quality';
import CustomSlider from '../CustomSlider'
import BalloonRowErrorBoundary from '../BalloonRowErrorBoundary'


const { Text } = Typography;

function useDebouncedCallback<T extends (...args: any[]) => void>(
	callback: T,
	delay: number
) {
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const debouncedFn = useCallback(
		(...args: Parameters<T>) => {
			if (timer.current) clearTimeout(timer.current);
			timer.current = setTimeout(() => {
				callback(...args);
				timer.current = null;
			}, delay);
		},
		[callback, delay]
	);
	useEffect(() => {
		return () => {
			if (timer.current) clearTimeout(timer.current);
		};
	}, []);
	return debouncedFn;
}

/** The control band the slider snaps to, as [min, max]; null when the balloon has no limits. */
const toleranceBand = (balloon: Balloon): [number, number] | null => {
	if (balloon.lower_control_limit == null || balloon.upper_control_limit == null) return null;
	const lo = Number(balloon.lower_control_limit);
	const hi = Number(balloon.upper_control_limit);
	if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
	return [Math.min(lo, hi), Math.max(lo, hi)];
};


interface InspectionResultsInputTableProps {
	balloons: Balloon[];
	isOperatorView?: boolean;
	currentSample: InspectionSample;
	unit?: string;
	controlsDisabled?: boolean;
	onBalloonClick?: (balloonId: string) => void;
}

const InspectionResultsInputTable: React.FC<InspectionResultsInputTableProps> = ({
	balloons,
	currentSample,
	unit,
	controlsDisabled,
	onBalloonClick,
}) => {
	const [updateInspectionResult] = useUpdateInspectionResultMutation();

	const [localResults, setLocalResults] = useState<InspectionResult[]>([]);
	const [originalValue, setOriginalValue] = useState<number | undefined>(undefined);
	const [resultErrors, setResultErrors] = useState<Record<string, boolean>>({});


	useEffect(() => {
		if (currentSample && currentSample.from_sample) {
			setLocalResults(currentSample.from_sample);
		}
	}, [currentSample?.id]);

	const debouncedUpdate = useDebouncedCallback((id: string, value: number, source?: string) => {
		updateInspectionResult({ id, value, source })
			.unwrap()
			.then(() => {
				setResultErrors((prev) => ({ ...prev, [id]: false }));
			})
			.catch((err: any) => {
				setResultErrors((prev) => ({ ...prev, [id]: true }));
				const detail =
					err?.data?.detail ||
					err?.data?.error ||
					(typeof err?.data === 'string' ? err.data : null) ||
					err?.error ||
					err?.message ||
					'Failed to update measurement';
				message.error(detail);
			});
	}, 50);

	const handleValueChange = useCallback(
		(balloonId: string, value: number, source: 'modal' | 'slider' | null) => {
			setLocalResults((prev) => {
				const existing = prev.find((item) => item.balloon === balloonId);
				if (existing) {
					return prev.map((item) =>
						item.balloon === balloonId ? { ...item, value, source } : item
					);
				}
				return prev;
			});
		},
		[]
	);

	const handleSliderChange = useCallback(
		(balloonId: string, val: number) => {
			handleValueChange(balloonId, val, 'slider');
		},
		[handleValueChange]
	);


	const [changedValues, setChangedValues] = useState<Record<string, boolean>>({});

	const handleSliderCommit = useCallback(
		(balloonId: string, value: number) => {
			const resultItem = localResults.find((r) => r.balloon === balloonId);
			if (resultItem) {
				// Ensure local state reflects the committed slider value/source
				setLocalResults((prev) =>
					prev.map((item) =>
						item.balloon === balloonId ? { ...item, value, source: 'slider' } : item
					)
				);
				debouncedUpdate(resultItem.id, value, 'slider');
			} else {
				console.warn('No matching inspection result for balloon:', balloonId);
			}
		},
		[localResults, debouncedUpdate]
	);

	const endColor = '#ffe5b4';
	const precision = unit === 'inch' ? 3 : 4;

	const generateMarks = useCallback(
		(balloon: Balloon) => {
			const marks: Record<string, string> = {};
			const lower = Number(balloon.lower_control_limit);
			const upper = Number(balloon.upper_control_limit);
			const nominal = Number(balloon.nominal);
			const decInterval = Number(balloon.decremental_interval);
			const incInterval = Number(balloon.incremental_interval);
			marks[nominal.toFixed(precision)] = nominal.toFixed(precision);
			for (let value = nominal - decInterval; value >= lower; value -= decInterval) {
				marks[value.toFixed(precision)] = ' ';
			}
			for (let value = nominal + incInterval; value <= upper; value += incInterval) {
				marks[value.toFixed(precision)] = ' ';
			}
			return marks;
		},
		[precision]
	);

	const calculateGradientPosition = useCallback(
		(min: number, max: number, nominal: number): number => {
			const range = max - min;
			return range === 0 ? 50 : ((nominal - min) / range) * 100;
		},
		[]
	);


	const [isModalOpen, setIsModalOpen] = useState(false);
	const [clickedBalloonId, setClickedBalloonId] = useState<string | null>(null);
	const [inputValue, setInputValue] = useState<number | undefined>(undefined);

	const openModal = useCallback(() => {
		setIsModalOpen(true);
	}, []);


	const closeModal = useCallback(() => {
		if (clickedBalloonId && inputValue !== undefined) {
			const updated = localResults.find((r) => r.balloon === clickedBalloonId);
			if (updated?.id) {
				updateInspectionResult({ id: updated.id, value: inputValue, source: 'modal' })
					.unwrap()
					.then(() => setResultErrors((prev) => ({ ...prev, [updated.id]: false })))
					.catch((err: any) => {
						setResultErrors((prev) => ({ ...prev, [updated.id]: true }));
						const detail =
							err?.data?.detail ||
							err?.data?.error ||
							(typeof err?.data === 'string' ? err.data : null) ||
							err?.error ||
							err?.message ||
							'Failed to update measurement';
						message.error(detail);
					});
			}
		}
		setIsModalOpen(false);
		setInputValue(undefined);
		setClickedBalloonId(null);
		setOriginalValue(undefined);
	}, [clickedBalloonId, inputValue, localResults, updateInspectionResult]);


	const handleModalCancel = useCallback(() => {
		if (clickedBalloonId && originalValue !== undefined) {
			setLocalResults((prev) =>
				prev.map((item) =>
					item.balloon === clickedBalloonId
						? { ...item, value: originalValue, source: undefined }
						: item
				)
			);
		}
		setIsModalOpen(false);
		setInputValue(undefined);
		setClickedBalloonId(null);
		setOriginalValue(undefined);
	}, [clickedBalloonId, originalValue]);

	const getEditButtonValue = useCallback(
		(balloon: Balloon) => {
			const obj = localResults.find((r) => r.balloon === balloon.id);

			if (
				obj &&
				obj.source === 'modal' &&
				obj.value !== undefined &&
				Number(obj.value) !== Number(balloon.nominal)
			) {
				return obj.value;
			}
			return '';
		},
		[localResults]
	);

	const handleEditClick = useCallback(
		(balloon: Balloon) => {
			const existing = localResults.find((r) => r.balloon === balloon.id);
			if (!existing) return;

			const orig = existing.value !== undefined ? existing.value : Number(balloon.nominal);
			setOriginalValue(orig);
			setClickedBalloonId(balloon.id);
			openModal();
			setInputValue(orig);
		},
		[localResults, openModal]
	);

	const handleLiveValueChange = useCallback(
		(value: number | null) => {
			const newValue = value === null ? undefined : value;
			setInputValue(newValue);
			setLocalResults((prev) =>
				prev.map((item) =>
					item.balloon === clickedBalloonId
						? { ...item, value: newValue, source: 'modal' }
						: item
				)
			);
		},
		[clickedBalloonId]
	);


	const relatedResult = useCallback(
		(balloon: Balloon) => {
			let result = {}
			const obj = localResults.find((r) => r.balloon === balloon.id);
			if (!obj || !obj.source) {
				result = {
					value: Number(balloon.nominal),
					source: 'nominal'
				}
				return result;
			}
			result = {
				value: obj.value,
				source: "result"
			}
			return result;

		},
		[localResults]
	);

	const getGoNoGoText = useCallback(
		(balloon: Balloon) => {
			const obj = localResults.find((r) => r.balloon === balloon.id);
			if (obj) {
				if (obj.passed === true) return 'Pass';
				// Legacy/Implicit check: if passed is null/undefined but we have a non-zero value, it's a Pass.
				if ((obj.passed === null || obj.passed === undefined) && obj.value && Number(obj.value) !== 0) {
					return 'Pass';
				}
			}
			return 'Not Inspected';
		},
		[localResults]
	);

	const renderRow = useCallback(
		(balloon: Balloon) => {

			const sliderResult = localResults.find((r) => r.balloon === balloon.id);
			const tooltipOpen = sliderResult?.source === 'modal' ? false : true;
			const isUnset = relatedResult(balloon).source === 'result';
			return (
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						width: '100%',
						justifyContent: 'center',
					}}
				>
					<Tooltip color="gray" title={balloon.gage_type_name}>
						<div
							onClick={() => onBalloonClick?.(balloon.id)}
							style={{
								borderRadius: '50%',
								border: '3px solid red',
								width: '2rem',
								height: '2rem',
								color: 'red',
								fontSize: '1rem',
								fontWeight: '600',
								cursor: onBalloonClick ? 'pointer' : 'default',
							}}
						>
							{balloon.balloon_number}
						</div>
					</Tooltip>
					<BalloonRowErrorBoundary fallbackValue={relatedResult(balloon).value}>
						<CustomSlider
							balloon={balloon}
							disabled={controlsDisabled || currentSample.hand_quality_status === "Completed"}
							source={relatedResult(balloon).source}
							value={relatedResult(balloon).value}
							onChange={(val) => handleSliderChange(balloon.id, val)}
							onCommit={(val) => handleSliderCommit(balloon.id, val)}
							unit={unit}
							error={sliderResult ? !!resultErrors[sliderResult.id] : false}
						/>
					</BalloonRowErrorBoundary>
					{balloon.measurement_strategy !== 'Keyence' && (
						<div>
							<Button
								type="primary"
								icon={<EditOutlined />}
								disabled={controlsDisabled || currentSample.hand_quality_status === "Completed"}
								style={{ backgroundColor: '#1890ff', borderColor: '#1890ff', color: 'white' }}
								onClick={() => handleEditClick(balloon)}
							>
								{getEditButtonValue(balloon)}
							</Button>
						</div>
					)}
				</div>
			);
		},
		[
			calculateGradientPosition,
			endColor,
			generateMarks,
			getEditButtonValue,
			handleEditClick,
			handleSliderChange,
			handleSliderCommit,
			relatedResult,
			localResults,
		]
	);

	const renderGoNoGo = useCallback(
		(balloon: Balloon) => {
			const currentStatus = getGoNoGoText(balloon);
			const tooltipText = balloon.measurement_strategy === "Visual" ? balloon.measurement_type : balloon.gage_type_name
			const handleChange = () => {
				const existing = localResults.find((r) => r.balloon === balloon.id);
				if (existing) {
					const newPassed = existing.passed !== true;
					updateInspectionResult({ id: existing.id, passed: newPassed, source: existing.source })
						.unwrap()
						.then(() => setResultErrors((prev) => ({ ...prev, [existing.id]: false })))
						.catch((err: any) => {
							setResultErrors((prev) => ({ ...prev, [existing.id]: true }));
							const detail =
								err?.data?.detail ||
								err?.data?.error ||
								(typeof err?.data === 'string' ? err.data : null) ||
								err?.error ||
								err?.message ||
								'Failed to update measurement';
							message.error(detail);
						});
					setLocalResults((prev) =>
						prev.map((item) =>
							item.balloon === balloon.id ? { ...item, passed: newPassed } : item
						)
					);
				} else {
					console.warn(
						'No matching inspection result record for Go/No-Go update for balloon:',
						balloon.id
					);
				}
			};
			return (
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						width: '100%',
						justifyContent: 'left',
					}}
				>
					<Tooltip color="gray" title={balloon.gage_type_name}>
						<div
							onClick={() => onBalloonClick?.(balloon.id)}
							style={{
								borderRadius: '50%',
								border: '3px solid red',
								width: '2rem',
								height: '2rem',
								color: 'red',
								fontSize: '1rem',
								fontWeight: '600',
								cursor: onBalloonClick ? 'pointer' : 'default',
							}}
						>
							{balloon.balloon_number}
						</div>
					</Tooltip>
					<Text strong style={{ marginRight: '10px' }}>
						{balloon.text_value}
					</Text>
					<Button
						type="primary"
						size="large"
						disabled={controlsDisabled || currentSample.hand_quality_status === "Completed"}
						style={{
							backgroundColor: (() => {
								const existing = localResults.find((r) => r.balloon === balloon.id);
								const isError = existing ? !!resultErrors[existing.id] : false;
								if (isError) return '#ff4d4f';
								return currentStatus === 'Pass' ? 'green' : 'gray';
							})(),
							borderColor: (() => {
								const existing = localResults.find((r) => r.balloon === balloon.id);
								const isError = existing ? !!resultErrors[existing.id] : false;
								if (isError) return '#ff4d4f';
								return currentStatus === 'Pass' ? 'green' : 'gray';
							})(),
							color: 'white',
						}}
						onClick={handleChange}
					>
						{currentStatus}
					</Button>
				</div>
			);
		},
		[localResults, getGoNoGoText, updateInspectionResult, currentSample, resultErrors]
	);

	// The slider can only land on marks inside the band; the typed value must obey the same limits.
	const editingBalloon = balloons.find((b) => b.id === clickedBalloonId);
	const editingBand = editingBalloon ? toleranceBand(editingBalloon) : null;
	const outOfTolerance =
		editingBand !== null &&
		inputValue !== undefined &&
		(Number(inputValue) < editingBand[0] || Number(inputValue) > editingBand[1]);

	if (!currentSample)
		return <Alert message="No sample selected" type="warning" />;

	return (
		<div>
			<List
				dataSource={balloons}
				renderItem={(balloon) => (
					<List.Item key={balloon.id} style={{ width: '100%' }}>
						{balloon.gage_type_is_pass_fail_gage || balloon.measurement_strategy === 'Visual' || balloon.measurement_type === "Other"
							? renderGoNoGo(balloon)
							: renderRow(balloon)}
					</List.Item>
				)}
			/>
			<Modal
				title="Enter measurement"
				open={isModalOpen}
				onOk={closeModal}
				okButtonProps={{ disabled: outOfTolerance }}
				onCancel={handleModalCancel}
				centered
				width="220px"
			>
				<InputNumber<number>
					value={inputValue}
					style={{ width: 160 }}
					min={0}
					status={outOfTolerance ? 'error' : undefined}
					onChange={handleLiveValueChange}
				/>
				{outOfTolerance && editingBand && (
					<Text type="danger" style={{ display: 'block', marginTop: 8 }}>
						Out of tolerance ({editingBand[0]} – {editingBand[1]})
					</Text>
				)}
			</Modal>
		</div>
	);
};

export default InspectionResultsInputTable;
